import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  CatalogEditorSheet,
  Checkbox,
  CommerceCatalogWorkspace,
  DataTable,
  DataTableToolbar,
  DescriptionText,
  HorizontalStack,
  ProductEditorSheet,
  PropertyGrid,
  PropertyItem,
  RegistryTableSurface,
  RegistryWorkspace,
  RegistryStatus,
  VerticalStack,
  toast,
  type CatalogEditorFormValue,
  type CatalogEditorViewModel,
  type ProductEditorFormValue,
  type ProductEditorViewModel,
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';

import {
  archiveProduct,
  createProduct,
  getCatalog,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listCatalogProducts,
  updateCatalog,
  updateProduct,
  type Catalog,
  type Product,
} from '@/lib/commerce';
import { buildCatalogDirectoryRoute, buildCatalogProductRoute } from '@/lib/commerce-routing';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery, withCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  catalogToForm,
  CommerceBreadcrumbHeader,
  CommerceSummarySurface,
  emptyCatalogForm,
  emptyProductForm,
  formatRelativeTime,
  metadataEntriesToRecord,
  productBrowseVisibilityOptions,
  productToForm,
  type ValidationMap,
  validateCatalogForm,
  validateProductForm,
  type EditorMode,
} from './catalogHierarchyShared';

const CATALOG_PRODUCT_SORT_FIELDS = [
  'created_at',
  'display_status',
  'name',
  'product_key',
  'product_type_key',
  'sort_order',
  'variants',
  'updated_at',
] as const;

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'published':
      return 'success';
    case 'draft':
    case 'unpublished':
      return 'warning';
    case 'archived':
    case 'inactive':
    case 'hidden':
      return 'muted';
    default:
      return 'default';
  }
}

export default function CatalogProductsPage({ catalogId }: { catalogId: string }) {
  const defaultPagination = { pageIndex: 0, pageSize: 10 };
  const { activeTenant } = useScope();
  const navigation = useNavigation();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    defaultPageSize: 10,
    facetKeys: [],
    sortFields: CATALOG_PRODUCT_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const requestSequence = useRef(0);

  const [catalogEditorOpen, setCatalogEditorOpen] = useState(false);
  const [catalogForm, setCatalogForm] = useState<CatalogEditorFormValue>(emptyCatalogForm);
  const [catalogValidation, setCatalogValidation] = useState<ValidationMap>({});

  const [productEditorMode, setProductEditorMode] = useState<EditorMode>('create');
  const [productEditorOpen, setProductEditorOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productForm, setProductForm] = useState<ProductEditorFormValue>(emptyProductForm);
  const [productValidation, setProductValidation] = useState<ValidationMap>({});
  const [savingKind, setSavingKind] = useState<'catalog' | 'product' | null>(null);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const pagination = query.pagination;

  const columns = useMemo<ColumnDef<Product>[]>(() => defineCommerceRegistryColumns<Product>([
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: 'Product',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <button
            type="button"
            onClick={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogProductRoute(catalogId, product.product_id), navigation.path))}
            className="group min-w-0 text-left"
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {product.name}
            </p>
            {product.short_description && (
              <DescriptionText className="line-clamp-1 text-[11px]">
                {product.short_description}
              </DescriptionText>
            )}
          </button>
        );
      },
    },
    {
      id: 'display_status',
      accessorKey: 'publish_status',
      header: 'Status',
      cell: ({ row }) => (
        <HorizontalStack gap={2}>
          <RegistryStatus label={row.original.publish_status} variant={statusVariant(row.original.publish_status)} />
          <RegistryStatus
            label={row.original.browse_visibility}
            variant={statusVariant(row.original.browse_visibility)}
          />
        </HorizontalStack>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'product_key',
      header: 'Key',
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">{row.original.product_key}</span>
      ),
    },
    {
      id: 'product_type_key',
      accessorFn: row => row.product_type_key || 'One-Time',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {row.original.product_type_key || 'One-Time'}
        </span>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id) || 'one_time')
      },
    },
    {
      id: 'variants',
      accessorFn: row => row.variants.length,
      header: 'Variants',
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">
          {row.original.variants.length}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatRelativeTime(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => openProductEdit(product)}
              aria-label={`Edit ${product.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => void deleteProductRecord(product)}
              aria-label={`Delete ${product.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:text-destructive hover:bg-destructive/5"
            >
              <Trash2 className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogProductRoute(catalogId, product.product_id), navigation.path))}
              aria-label={`View variants in ${product.name}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-foreground transition-all hover:bg-muted"
            >
              Variants
              <ArrowRight className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], {
    mode: 'manual',
    registry: 'catalog-products',
    sortFields: CATALOG_PRODUCT_SORT_FIELDS,
  }), [catalogId, navigation]);

  const normalizedSearch = searchQuery.trim();

  const handlePaginationChange: OnChangeFn<{ pageIndex: number; pageSize: number }> = (updater) => {
    query.onPaginationChange(updater);
  };

  const loadPage = async () => {
    const requestID = ++requestSequence.current;
    if (!activeTenant?.id) {
      setCatalog(null);
      setProducts([]);
      setTotalProducts(0);
      setError(null);
      setAccessDeniedStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);

    try {
      const paginationQuery = toLimitOffset(pagination);
      const [nextCatalog, productPage] = await Promise.all([
        getCatalog(catalogId),
        listCatalogProducts(catalogId, { ...paginationQuery, search: normalizedSearch, sort: query.sort }),
      ]);
      if (requestID !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(productPage.total_count, pagination.pageSize);
      if (pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setCatalog(nextCatalog);
      setProducts(productPage.items);
      setTotalProducts(productPage.total_count);
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load catalog products.'));
      }
      setCatalog(null);
      setProducts([]);
      setTotalProducts(0);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadPage();
  }, [activeTenant?.id, catalogId, pagination.pageIndex, pagination.pageSize, normalizedSearch, query.sort]);

  useEffect(() => {
    query.onPaginationChange(defaultPagination);
    setRowSelection({});
  }, [catalogId]);

  useEffect(() => {
    setRowSelection({});
  }, [pagination.pageIndex, pagination.pageSize]);

  const handleSearchChange = (value: string) => {
    query.setSearch(value);
    setRowSelection({});
  };

  const openCatalogEdit = () => {
    if (!catalog) {
      return;
    }
    setCatalogForm(catalogToForm(catalog));
    setCatalogValidation({});
    setCatalogEditorOpen(true);
  };

  const openProductCreate = () => {
    setProductEditorMode('create');
    setEditingProductId(null);
    setProductForm(emptyProductForm());
    setProductValidation({});
    setProductEditorOpen(true);
  };

  const openProductEdit = (product: Product) => {
    setProductEditorMode('edit');
    setEditingProductId(product.product_id);
    setProductForm(productToForm(product));
    setProductValidation({});
    setProductEditorOpen(true);
  };

  const saveCatalog = async () => {
    if (!catalog) {
      return;
    }

    const nextValidation = validateCatalogForm(catalogForm);
    setCatalogValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('catalog');
    setError(null);

    try {
      await updateCatalog(catalog.catalog_id, {
        catalog_owner_tenant_id: catalog.catalog_owner_tenant_id,
        catalog_key: catalogForm.identity.catalogKey.trim(),
        name: catalogForm.identity.name.trim(),
        status: catalogForm.lifecycle.status,
        default_currency: catalogForm.lifecycle.defaultCurrency || '',
        description: catalogForm.description || '',
        metadata: metadataEntriesToRecord(catalogForm.metadata),
      });
      await loadPage();
      setCatalogEditorOpen(false);
      toast.success('Catalog updated', { description: catalog.name });
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to save catalog.');
        setError(msg);
        toast.error('Failed to save catalog', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const saveProduct = async () => {
    if (!catalog) {
      return;
    }

    const nextValidation = validateProductForm(productForm);
    setProductValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    const payload = {
      name: productForm.identity.name.trim(),
      short_description: productForm.identity.shortDescription || '',
      description: productForm.identity.description || '',
      status: productForm.lifecycle.status,
      publish_status: productForm.lifecycle.publishStatus,
      browse_visibility: productForm.lifecycle.browseVisibility,
      product_type_key: productForm.classification.productTypeId || '',
      service_class: productForm.classification.serviceClass || '',
      service_group: productForm.classification.serviceGroup || '',
      default_variant_id: productForm.defaultVariantId || '',
      sort_order: productForm.lifecycle.sortOrder,
      facets: productForm.classification.facets
        .filter(item => item.key.trim() && item.value.trim())
        .map(item => ({
          classification_key: item.key.trim(),
          classification_value: item.value.trim(),
          sort_order: item.sortOrder,
        })),
      flags: productForm.classification.flags
        .filter(item => item.key.trim() && item.value.trim())
        .map(item => ({
          classification_key: item.key.trim(),
          classification_value: item.value.trim(),
          sort_order: item.sortOrder,
        })),
      metadata: metadataEntriesToRecord(productForm.metadata),
    };

    setSavingKind('product');
    setError(null);

    try {
      const saved =
        productEditorMode === 'edit' && editingProductId
          ? await updateProduct(editingProductId, payload)
          : await createProduct(catalog.catalog_id, payload);
      await loadPage();
      setProductEditorOpen(false);
      toast.success(productEditorMode === 'edit' ? 'Product updated' : 'Product created', {
        description: saved.name,
      });
      navigation.navigate(withCommerceRegistryQuery(buildCatalogProductRoute(catalog.catalog_id, saved.product_id), navigation.path));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to save product.');
        setError(msg);
        toast.error('Failed to save product', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteProductRecord = async (product: Product) => {
    if (!catalog) {
      return;
    }
    if (!window.confirm(`Delete product "${product.name}"?`)) {
      return;
    }

    setSavingKind('product');
    setError(null);
    try {
      await archiveProduct(product.product_id);
      await loadPage();
      toast.success('Product deleted', {
        description: product.name,
      });
      if (editingProductId === product.product_id) {
        setProductEditorOpen(false);
        setEditingProductId(null);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete product.');
        setError(msg);
        toast.error('Failed to delete product', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    searchQuery,
    onSearchChange: handleSearchChange,
    error,
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'product-create',
      label: 'New Product',
      onAction: catalog ? openProductCreate : undefined,
      disabled: !catalog,
    },
    metrics: [
      {
        id: 'catalog-key',
        label: 'Catalog Key',
        value: catalog?.catalog_key || '...',
        helperText: 'Unique identifier for this catalog library.',
      },
      {
        id: 'catalog-currency',
        label: 'Currency',
        value: catalog?.default_currency || 'USD',
        helperText: 'Default commercial currency.',
      },
      {
        id: 'product-total',
        label: 'Loaded Products',
        value: String(products.length),
        helperText: 'Rows currently loaded for this registry page.',
      },
      {
        id: 'product-published',
        label: 'Loaded Published',
        value: String(products.filter(product => product.publish_status.toLowerCase() === 'published').length),
        tone: 'success',
        helperText: 'Published rows in the current loaded page window.',
      },
    ],
  });

  const catalogEditorViewModel: CatalogEditorViewModel = {
    mode: 'edit',
    state: savingKind === 'catalog' ? 'saving' : 'ready',
    isOpen: catalogEditorOpen,
    title: 'Edit Catalog',
    subtitle: 'Update the catalog identity and publishing defaults.',
    saveActionLabel: 'Save Catalog',
    catalogId: catalog?.catalog_id || null,
    value: catalogForm,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    currencyOptions: [
      { value: 'USD', label: 'USD' },
      { value: 'CAD', label: 'CAD' },
      { value: 'EUR', label: 'EUR' },
    ],
    fieldHelp: [],
    validationErrors: catalogValidation,
    onChange: patch => setCatalogForm(current => ({ ...current, ...patch })),
    onSave: saveCatalog,
    onClose: () => setCatalogEditorOpen(false),
  };

  const productEditorViewModel: ProductEditorViewModel = {
    mode: productEditorMode,
    state: savingKind === 'product' ? 'saving' : 'ready',
    isOpen: productEditorOpen,
    title: productEditorMode === 'edit' ? 'Edit Product' : 'Create Product',
    subtitle:
      productEditorMode === 'edit'
        ? 'Refine the merchandising metadata for this product.'
        : 'Add a new product to this catalog.',
    saveActionLabel: productEditorMode === 'edit' ? 'Save Product' : 'Create Product',
    productId: editingProductId,
    value: productForm,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    publishStatusOptions: [
      { value: 'unpublished', label: 'Unpublished' },
      { value: 'published', label: 'Published' },
    ],
    browseVisibilityOptions: [...productBrowseVisibilityOptions],
    productTypeOptions: [
      { value: '', label: 'Unset' },
      { value: 'one_time', label: 'One-Time' },
      { value: 'subscription', label: 'Subscription' },
      { value: 'bundle', label: 'Bundle' },
    ],
    serviceClassOptions: [
      { value: '', label: 'Unset' },
      { value: 'internet', label: 'Internet' },
      { value: 'voice', label: 'Voice' },
      { value: 'video', label: 'Video' },
      { value: 'managed_service', label: 'Managed Service' },
    ],
    serviceGroupOptions: [
      { value: '', label: 'Unset' },
      { value: 'residential', label: 'Residential' },
      { value: 'business', label: 'Business' },
      { value: 'enterprise', label: 'Enterprise' },
    ],
    defaultVariantOptions: [
      { value: '', label: 'No default variant' },
      ...products
        .find(product => product.product_id === editingProductId)
        ?.variants.map(variant => ({
          value: variant.variant_id,
          label: variant.display_name || variant.name,
        })) || [],
    ],
    fieldHelp: [],
    validationErrors: productValidation,
    onChange: patch => setProductForm(current => ({ ...current, ...patch })),
    onSave: saveProduct,
    onClose: () => setProductEditorOpen(false),
  };

  return (
    <>
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[
              { label: 'Catalogs', onSelect: () => navigation.navigate(withCommerceRegistryQuery(buildCatalogDirectoryRoute(), navigation.path)) },
              { label: catalog?.name || 'Catalog' },
            ]}
            title={catalog?.name || 'Catalog'}
            description="Manage the products contained within this catalog library."
            onBack={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogDirectoryRoute(), navigation.path))}
          />
        }
      >
        <RegistryWorkspace>
          <RegistryTableSurface
            title="Product Inventory"
            description="Open a product to manage its variants. Catalog settings remain available to refine publishing defaults."
            actions={
              catalog ? (
                <button
                  type="button"
                  onClick={openCatalogEdit}
                  className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-background px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Catalog Settings
                </button>
              ) : undefined
            }
            count={totalProducts}
          >
            <DataTable
              mode="manual"
              columns={columns}
              data={products}
              sorting={sorting}
              onSortingChange={updater => {
                const next = typeof updater === 'function' ? updater(sorting) : updater;
                query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
              }}
              globalFilter={searchQuery}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              manualPagination={true}
              totalRowCount={totalProducts}
              loading={loading}
              className="border-none rounded-none"
              showPagination={true}
              emptyState={
                <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
                  <p className="text-sm font-black tracking-tight text-foreground">No products found</p>
                  <DescriptionText className="mt-1.5 max-w-xs text-xs leading-relaxed">
                    Create the first product in this catalog to begin variant merchandising.
                  </DescriptionText>
                  {catalog ? (
                    <button
                      type="button"
                      onClick={openProductCreate}
                      className="mt-5 inline-flex items-center rounded-md bg-primary px-4 py-2 text-[11px] font-black uppercase tracking-wider text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-95"
                    >
                      New Product
                    </button>
                  ) : null}
                </div>
              }
              noResultsState={
                <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
                  <p className="text-sm font-black tracking-tight text-foreground">No products found</p>
                  <DescriptionText className="mt-1.5 max-w-xs text-xs leading-relaxed">
                    No products matched the current search.
                  </DescriptionText>
                </div>
              }
              renderToolbar={(table) => (
                <DataTableToolbar
                  table={table}
                  searchValue={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchPlaceholder="Search all catalog products..."
                  resultCount={totalProducts}
                />
              )}
            />
          </RegistryTableSurface>
        </RegistryWorkspace>
      </CommerceCatalogWorkspace>

      <CatalogEditorSheet editor={catalogEditorViewModel} />
      <ProductEditorSheet editor={productEditorViewModel} />
    </>
  );
}
